import React, { useContext, useEffect } from 'react';
import Container from '../../atoms/Container';
import { useInView } from 'react-intersection-observer';

import { purify } from '../../utils/helpers';
import {
  ComponentWrapper,
  StyledHeader,
  TableWrapper,
  Benefit,
  BenefitContent,
  BenefitStatus,
  BenefitStatusText,
  Plan,
  PlanInfo,
  PlanName,
  BenefitsRow,
  BenefitsBody,
  StyledButton,
} from './components';
import { Experimental__IconCheckAltFill } from '@spotify-internal/encore-web';
import {
  eventParamAttributes,
  EventParamTypes,
  inViewEventAttributes,
} from '@spotify-internal/touchdown-analytics';
import BenefitsComparisonTooltip from '../../atoms/BenefitsComparisonTooltip';
import MarketContext from '../../../configuration/context/market';
import UserContext from '../../../configuration/context/user';
import { trackEvent } from '../../../lib/tracking';
import {
  componentViewEvent,
  premiumBenefitsComparisonTooltipHoverEvent,
} from '../../../lib/tracking/eventPayloadDefinitions';
import { getCookie } from '../../../lib/cookie-utils';

export type HighlightColor =
  | {
      type: 'solid';
      value: string;
    }
  | {
      type: 'gradient';
      value: {
        from: string;
        to: string;
      };
    };

export type BenefitsComparisonPropType = {
  header?: React.ComponentProps<typeof StyledHeader>;
  labels: {
    [key: string]: string;
  };
  container?: React.ComponentProps<typeof Container>;
  plans: {
    name: string;
    benefits: (boolean | string)[];
    eventParams?: EventParamTypes;
    $planColor?: string;
    highlight?: boolean;
    useBottomBorders?: boolean;
  }[];
  benefits: {
    description: string;
    details?: string;
    id: string;
  }[];
  button: React.ComponentProps<typeof StyledButton>;
  highlightColor?: HighlightColor;
};

export function BenefitsComparison({
  header,
  labels,
  container,
  highlightColor,
  plans,
  benefits,
  button,
}: BenefitsComparisonPropType) {
  const componentName = 'benefits-comparison';
  const componentType = 'product';
  const market = useContext(MarketContext);
  const user = useContext(UserContext);

  const { ref, inView } = useInView({
    fallbackInView: true,
    threshold: 0.6,
    triggerOnce: true,
  });

  // When the component is inView, this code will create a componentViewEvent to track components impressions(event-definition: MarketingResourceViewEvent)
  useEffect(() => {
    if (inView === true) {
      const payload = {
        resource_name: 'premium',
        component_name: 'premium_tweak_benefits_comparison',
        country: market?.country ?? 'unknown',
        locale: market?.language ?? 'unknown',
        full_url: window.location.href,
        correlation_id: getCookie('sp_t') ?? 'unknown',
        is_evergreen: market?.isEvergreen,
        eligibility: user?.eligibility,
      };

      trackEvent(
        componentViewEvent(payload, {
          authorize: user.authenticated,
        }),
      );
    }
  }, [market, user, inView]);

  // When one of the tooltips are hovered on, this code should create a TooltipHoverEvent to track components interaction(event-definition: premiumBenefitsComparisonTooltipHoverEvent)
  const handleBenefitsMouseOverOrFocus = (
    e: React.MouseEvent<HTMLDivElement> | React.FocusEvent<HTMLDivElement>,
  ) => {
    if (inView === true) {
      const payload = {
        tooltip_interaction: `User saw benefit: ${
          e.currentTarget.querySelector('span')?.innerText
        }`,
        country: market?.country ?? 'unknown',
        locale: market?.language ?? 'unknown',
        full_url: window.location.href,
        correlation_id: getCookie('sp_t') ?? 'unknown',
        timestamp: Math.floor(Date.now() / 1000),
        is_evergreen: market?.isEvergreen,
        eligibility: user?.eligibility,
      };

      trackEvent(
        premiumBenefitsComparisonTooltipHoverEvent(payload, {
          authorize: user.authenticated,
        }),
      );
    }
    e.stopPropagation();
  };

  return (
    <Container disablePaddings={{ before: true, after: true }} {...container}>
      <ComponentWrapper>
        {header && <StyledHeader textAlign="left" {...header} />}
        <TableWrapper highlightColor={highlightColor}>
          <thead ref={ref}>
            <tr>
              <Benefit id="benefit-0" as="th" scope="col">
                <BenefitContent>{labels.BENEFITS_HEADING}</BenefitContent>
              </Benefit>
              {plans.map(
                (
                  {
                    name,
                    highlight,
                    $planColor,
                    eventParams,
                    useBottomBorders,
                  },
                  index,
                ) => (
                  <Plan
                    as="th"
                    scope="col"
                    key={`benefit-${name}`}
                    highlight={highlight}
                    index={index + 1}
                    $planColor={$planColor}
                    useBottomBorders={!!useBottomBorders && useBottomBorders}
                    {...eventParamAttributes({
                      ...eventParams,
                      componentName,
                      componentPosition: index,
                      componentType,
                    })}
                    {...inViewEventAttributes()}
                  >
                    <PlanInfo>
                      <PlanName
                        key={`plan-${index}`}
                        dangerouslySetInnerHTML={{ __html: purify(name) }}
                      />
                    </PlanInfo>
                  </Plan>
                ),
              )}
            </tr>
          </thead>
          <BenefitsBody>
            {benefits.map(({ description, details }, benefitIndex) => (
              <BenefitsRow
                key={`benefit-list-${benefitIndex}`}
                benefitIndex={benefitIndex + 1}
                inView={inView}
              >
                <Benefit as="th" scope="row" id={`benefit-${benefitIndex + 1}`}>
                  <BenefitContent
                    onMouseOver={handleBenefitsMouseOverOrFocus}
                    onFocus={handleBenefitsMouseOverOrFocus}
                  >
                    <BenefitsComparisonTooltip content={details ? details : ''}>
                      {description}
                    </BenefitsComparisonTooltip>
                  </BenefitContent>
                </Benefit>
                {plans.map(
                  (
                    { benefits: planBenefits, highlight, useBottomBorders },
                    planIndex,
                  ) => (
                    <BenefitStatus
                      key={`benefitStatus-${planIndex}`}
                      headers={`benefit-${benefitIndex + 1}`}
                      aria-labelledby={`benefit-${benefitIndex + 1}`}
                      highlight={highlight}
                      useBottomBorders={!!useBottomBorders && useBottomBorders}
                    >
                      {planBenefits[benefitIndex] === true && (
                        <span role="img" aria-label={labels.INCLUDED}>
                          <Experimental__IconCheckAltFill
                            iconSize={32}
                            aria-label={labels.INCLUDED}
                          />
                        </span>
                      )}
                      {!planBenefits[benefitIndex] && (
                        <svg
                          width="24"
                          height="4"
                          viewBox="0 0 24 4"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <title>not included</title>
                          <rect
                            x="0.513672"
                            y="0.826172"
                            width="22.8767"
                            height="3"
                            fill="#949494"
                          />
                        </svg>
                      )}
                      {typeof planBenefits[benefitIndex] === 'string' && (
                        <BenefitStatusText
                          dangerouslySetInnerHTML={{
                            __html: purify(
                              planBenefits[benefitIndex] as string,
                            ),
                          }}
                        />
                      )}
                    </BenefitStatus>
                  ),
                )}
              </BenefitsRow>
            ))}
          </BenefitsBody>
        </TableWrapper>
        {button && (
          <StyledButton
            children={button.children}
            href={button.href}
            label={button.label}
            insertMarket={button.insertMarket}
            backgroundColor={button.backgroundColor}
            $borderValues={button.$borderValues}
          />
        )}
      </ComponentWrapper>
    </Container>
  );
}

export default BenefitsComparison;
